import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AuthDrawerProvider } from './context/AuthDrawerContext';
import Sidebar      from './components/Sidebar';
import HelpWidget   from './components/HelpWidget';
import PriyaChatbot from './components/PriyaChatbot';
import ProGate      from './components/ProGate';
import LandingPage  from './pages/LandingPage';
import VerifyEmailPage    from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage  from './pages/ResetPasswordPage';
import RestorePage        from './pages/RestorePage';
import VerifyBanner       from './components/VerifyBanner';
import DashboardPage from './pages/DashboardPage';
import DealsPage    from './pages/DealsPage';
import CalendarPage from './pages/CalendarPage';
import IdeasPage    from './pages/IdeasPage';
import InvoicesPage  from './pages/InvoicesPage';
import MediaKitPage  from './pages/MediaKitPage';
import RateCardPage  from './pages/RateCardPage';
import PaymentsPage   from './pages/PaymentsPage';
import AnalyticsPage  from './pages/AnalyticsPage';
import ContractsPage  from './pages/ContractsPage';
import RepurposePage  from './pages/RepurposePage';
import OutreachPage   from './pages/OutreachPage';
import TrendingPage   from './pages/TrendingPage';
import CollabPage     from './pages/CollabPage';
import BioPage        from './pages/BioPage';
import PublicBioPage  from './pages/PublicBioPage';
import CaptionPage    from './pages/CaptionPage';
import IssuesPage     from './pages/IssuesPage';
import MyAccountPage  from './pages/MyAccountPage';
import TermsPage      from './pages/TermsPage';
import PrivacyPage    from './pages/PrivacyPage';
import RefundPage     from './pages/RefundPage';
// Pricing hidden 2026-06-21 — re-enable when we charge again (restore this import + the /pricing route below + nav links)
// import PricingPage    from './pages/PricingPage';
import ContactPage    from './pages/ContactPage';
import HelpPage       from './pages/HelpPage';
import RateCardToolPage from './pages/RateCardToolPage';
import YtMoneyToolPage from './pages/YtMoneyToolPage';
import GstTaxToolPage from './pages/GstTaxToolPage';
import InvoiceToolPage from './pages/InvoiceToolPage';
import EngagementToolPage from './pages/EngagementToolPage';
import ContractToolPage from './pages/ContractToolPage';
import BrandDealPriceToolPage from './pages/BrandDealPriceToolPage';
import InstagramMoneyToolPage from './pages/InstagramMoneyToolPage';
import './index.css';

function ProtectedLayout({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--off-white)' }}>
      <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.2rem', color: 'var(--ink)' }}>
        <span style={{ marginRight: '0.5rem' }}>●</span> CreatorKhata
      </div>
    </div>
  );
  if (!user) return <Navigate to="/" replace />;
  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <VerifyBanner />
        {children}
      </div>
      <HelpWidget />
    </div>
  );
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <AuthProvider>
        <BrowserRouter>
          <AuthDrawerProvider>
          <Routes>
            <Route path="/"      element={<PublicRoute><LandingPage /></PublicRoute>} />
            <Route path="/login"  element={<Navigate to="/?auth=signin" replace />} />
            <Route path="/signup" element={<Navigate to="/?auth=signup" replace />} />
            <Route path="/verify-email"    element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password"  element={<ResetPasswordPage />} />
            <Route path="/restore"         element={<RestorePage />} />
            {/* Public legal pages — required by Razorpay + good production hygiene */}
            {/* Pricing hidden 2026-06-21 — redirect to home; restore <Route ... element={<PricingPage />} /> to re-enable */}
            <Route path="/pricing"      element={<Navigate to="/" replace />} />
            <Route path="/help"         element={<HelpPage />} />
            <Route path="/terms"        element={<TermsPage />} />
            <Route path="/privacy"      element={<PrivacyPage />} />
            <Route path="/refund"       element={<RefundPage />} />
            <Route path="/cancellation" element={<RefundPage />} />
            <Route path="/contact"      element={<ContactPage />} />
            {/* Public lead-magnet tool — no login, SEO entry point */}
            <Route path="/tools/rate-card-generator" element={<RateCardToolPage />} />
            <Route path="/tools/youtube-money-calculator" element={<YtMoneyToolPage />} />
            <Route path="/tools/creator-gst-tax-calculator" element={<GstTaxToolPage />} />
            <Route path="/tools/influencer-invoice-generator" element={<InvoiceToolPage />} />
            <Route path="/tools/engagement-rate-calculator" element={<EngagementToolPage />} />
            <Route path="/tools/brand-deal-contract-generator" element={<ContractToolPage />} />
            <Route path="/tools/brand-deal-price-calculator" element={<BrandDealPriceToolPage />} />
            <Route path="/tools/instagram-money-calculator" element={<InstagramMoneyToolPage />} />
            <Route path="/dashboard" element={<ProtectedLayout><DashboardPage /></ProtectedLayout>} />
            <Route path="/deals"     element={<ProtectedLayout><DealsPage /></ProtectedLayout>} />
            <Route path="/invoices"  element={<ProtectedLayout><ProGate feature="GST Invoices"        description="Generate compliant GST invoices with auto-calculated tax, TDS sections, and PDF export. Available on PRO."><InvoicesPage  /></ProGate></ProtectedLayout>} />
            <Route path="/mediakit"  element={<ProtectedLayout><ProGate feature="Media Kit"           description="Build a brand-pitch media kit (audience, rates, sample work) and export it as a PDF to send to brands. PRO only."><MediaKitPage  /></ProGate></ProtectedLayout>} />
            <Route path="/ratecard"  element={<ProtectedLayout><ProGate feature="Rate Card"           description="Publish a polished rate card with per-platform pricing and bundles, ready to share with prospects. PRO only."><RateCardPage  /></ProGate></ProtectedLayout>} />
            <Route path="/payments"  element={<ProtectedLayout><ProGate feature="TDS / Payment Tracker" description="Track invoices, payments received, and TDS deducted — auto-calculated for ITR filing. PRO only."><PaymentsPage  /></ProGate></ProtectedLayout>} />
            <Route path="/analytics"  element={<ProtectedLayout><AnalyticsPage /></ProtectedLayout>} />
            <Route path="/contracts" element={<ProtectedLayout><ProGate feature="Contracts"           description="Generate platform-compliant contracts with deliverables, payment terms, and IP clauses. Export as PDF. PRO only."><ContractsPage /></ProGate></ProtectedLayout>} />
            <Route path="/repurpose" element={<ProtectedLayout><ProGate feature="Repurpose"           description="Turn one piece of content into 8 platform-tailored versions (YouTube Short, Reel, Twitter thread, blog, email newsletter). PRO only."><RepurposePage /></ProGate></ProtectedLayout>} />
            <Route path="/outreach"  element={<ProtectedLayout><ProGate feature="Outreach Templates"  description="Send polished cold-pitch, follow-up, and negotiation emails to brands — with templates built for Indian creators. PRO only."><OutreachPage  /></ProGate></ProtectedLayout>} />
            <Route path="/trending"   element={<ProtectedLayout><TrendingPage /></ProtectedLayout>} />
            <Route path="/collabs"   element={<ProtectedLayout><ProGate feature="Collabs Tracker"     description="Track creator-to-creator collabs separately from brand deals — joint reels, podcast guesting, cross-promotion. PRO only."><CollabPage    /></ProGate></ProtectedLayout>} />
            <Route path="/bio"        element={<ProtectedLayout><BioPage /></ProtectedLayout>} />
            <Route path="/bio/:slug"  element={<PublicBioPage />} />
            <Route path="/captions"   element={<ProtectedLayout><CaptionPage /></ProtectedLayout>} />
            <Route path="/calendar"  element={<ProtectedLayout><CalendarPage /></ProtectedLayout>} />
            <Route path="/ideas"     element={<ProtectedLayout><IdeasPage /></ProtectedLayout>} />
            <Route path="/account"   element={<ProtectedLayout><MyAccountPage /></ProtectedLayout>} />
            <Route path="/issues"    element={<ProtectedLayout><IssuesPage /></ProtectedLayout>} />
            <Route path="*"          element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Analytics />
          <PriyaChatbot />
          </AuthDrawerProvider>
        </BrowserRouter>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}
